/**
 * What is about to change for a patient who is already running a program.
 *
 * Publishing replaces what someone does tomorrow with what is on screen now,
 * and until this existed the confirmation showed only a version number — the
 * doctor had to remember what he had altered, possibly across several sittings
 * with an autosaved draft in between. Remembering is not a safety mechanism.
 *
 * Everything here is driven by each exercise's own `ParamSpec` declarations:
 * the labels, the units, and the fact that duration is stored in seconds and
 * read in minutes all come from the contract. There is no exercise-specific
 * code in this file and there must never be (§1).
 */

import {
  displayUnitOf,
  getDefinition,
  toDisplayValue,
  INPUT_MODE_LABELS,
  POSITION_LABELS,
  type ParamSpec,
} from '@pathways/exercises';
import type { PrescribedExercise, ProgramSessionDraft } from '../../api.js';

export interface SessionChanges {
  label: string;
  lines: string[];
}

/**
 * Compare a published program against the draft about to replace it.
 *
 * Returns one entry per session that differs, and nothing at all when the two
 * are identical — which is itself worth showing, because publishing an
 * unchanged program adds a version to the patient's history that means nothing.
 */
export function summariseChanges(
  published: ProgramSessionDraft[] | null,
  draft: ProgramSessionDraft[],
): SessionChanges[] {
  if (!published) {
    return draft.map((session) => ({
      label: session.label,
      lines: session.exercises.map((e) => `Added ${exerciseName(e)}.`),
    }));
  }

  const labels = [
    ...new Set([...published.map((s) => s.label), ...draft.map((s) => s.label)]),
  ].sort((a, b) => a.localeCompare(b));

  const out: SessionChanges[] = [];

  for (const label of labels) {
    const before = published.find((s) => s.label === label);
    const after = draft.find((s) => s.label === label);

    if (!after) {
      out.push({ label, lines: ['Removed from the program.'] });
      continue;
    }
    if (!before) {
      out.push({
        label,
        lines: [
          'New session.',
          ...after.exercises.map((e) => `Added ${exerciseName(e)}.`),
        ],
      });
      continue;
    }

    const lines = sessionLines(before.exercises, after.exercises);
    if (lines.length > 0) out.push({ label, lines });
  }

  return out;
}

function sessionLines(
  before: PrescribedExercise[],
  after: PrescribedExercise[],
): string[] {
  const lines: string[] = [];

  const beforeIds = before.map((e) => e.exerciseId);
  const afterIds = after.map((e) => e.exerciseId);
  const sameSet =
    beforeIds.length === afterIds.length &&
    [...beforeIds].sort().join('|') === [...afterIds].sort().join('|');
  if (sameSet && beforeIds.join('|') !== afterIds.join('|')) {
    lines.push('The order changed.');
  }

  // Pair by exercise, in order of appearance, so a reordered program reports
  // what actually changed inside each exercise rather than a wall of
  // replacements. Anything left unpaired was genuinely added or removed.
  const unpaired = [...before];
  for (const row of after) {
    const index = unpaired.findIndex((e) => e.exerciseId === row.exerciseId);
    if (index === -1) {
      lines.push(`Added ${exerciseName(row)}.`);
      continue;
    }
    const [match] = unpaired.splice(index, 1);
    if (!match) continue;
    const changes = exerciseChanges(match, row);
    if (changes.length > 0) {
      lines.push(`${exerciseName(row)} — ${changes.join(', ')}`);
    }
  }
  for (const row of unpaired) {
    lines.push(`Removed ${exerciseName(row)}.`);
  }

  return lines;
}

function exerciseChanges(
  before: PrescribedExercise,
  after: PrescribedExercise,
): string[] {
  const changes: string[] = [];

  if (before.position !== after.position) {
    changes.push(
      `position ${POSITION_LABELS[before.position]} → ${POSITION_LABELS[after.position]}`,
    );
  }
  if (before.inputMode !== after.inputMode) {
    changes.push(
      `responds with ${INPUT_MODE_LABELS[before.inputMode]} → ` +
        `${INPUT_MODE_LABELS[after.inputMode]}`,
    );
  }

  const noteBefore = (before.patientNote ?? '').trim();
  const noteAfter = (after.patientNote ?? '').trim();
  if (noteBefore !== noteAfter) {
    // The note itself is not quoted here: it is the patient's own wording and
    // this list is read at a glance, not proofread.
    if (!noteBefore) changes.push('note to the patient added');
    else if (!noteAfter) changes.push('note to the patient removed');
    else changes.push('note to the patient changed');
  }

  const definition = getDefinition(after.exerciseId);
  if (definition) {
    for (const [key, spec] of Object.entries(definition.params)) {
      const was = before.params[key];
      const now = after.params[key];
      if (was === undefined || now === undefined || was === now) continue;
      changes.push(`${lower(spec.label)} ${show(spec, was)} → ${show(spec, now)}`);
    }
  }

  return changes;
}

function exerciseName(row: PrescribedExercise): string {
  return getDefinition(row.exerciseId)?.name ?? row.exerciseId;
}

/**
 * A value as the doctor reads it on the form: minutes rather than seconds,
 * "Grey" rather than #808080, "on" rather than true.
 */
function show(spec: ParamSpec, value: number | string | boolean): string {
  switch (spec.kind) {
    case 'number': {
      if (typeof value !== 'number') return String(value);
      const unit = displayUnitOf(spec);
      const shown = toDisplayValue(spec, value);
      return unit ? `${shown} ${unit}` : String(shown);
    }
    case 'enum':
      return spec.options.find((o) => o.value === value)?.label ?? String(value);
    case 'boolean':
      return value ? 'on' : 'off';
    default:
      return String(value);
  }
}

/** Parameter labels are capitalised for a form field; mid-sentence they are not. */
function lower(label: string): string {
  return label.charAt(0).toLowerCase() + label.slice(1);
}
